import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight, Inbox } from "lucide-react";

export default function DataTable({
  columns, rows, searchable = true, searchKeys = [], pageSize = 20,
  onRowClick, actions, emptyText = "لا توجد بيانات",
  testId = "data-table", toolbar,
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!q || !searchKeys.length) return rows || [];
    const lc = q.toLowerCase();
    return (rows || []).filter((r) =>
      searchKeys.some((k) => String(r?.[k] ?? "").toLowerCase().includes(lc))
    );
  }, [rows, q, searchKeys]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="card-warm overflow-hidden" data-testid={testId}>
      {(searchable || toolbar) && (
        <div
          className="flex items-center justify-between gap-4 px-5 py-3.5"
          style={{ borderBottom: "1px solid #EEF3EF" }}
        >
          {searchable ? (
            <div className="relative w-full max-w-sm">
              <Search className="absolute end-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.75} />
              <Input
                placeholder="بحث…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                className="pe-10 h-10 bg-[#F8FAF7] border-[#E4E9E5] rounded-xl text-[13px] focus:bg-white"
                data-testid={`${testId}-search`}
              />
            </div>
          ) : <div />}
          {toolbar}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr style={{ background: "#F8FAF7", borderBottom: "1px solid #E4E9E5" }}>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={
                    "px-5 py-3 text-start text-[10.5px] font-bold text-slate-500 uppercase tracking-[0.1em] whitespace-nowrap " +
                    (c.align === "end" ? "text-end " : "") +
                    (c.headerClassName || "")
                  }
                >
                  {c.header}
                </th>
              ))}
              {actions && (
                <th className="px-5 py-3 text-end text-[10.5px] font-bold text-slate-500 uppercase tracking-[0.1em] w-24">
                  إجراءات
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-20 text-center">
                  <div className="icon-chip h-14 w-14 mx-auto mb-3 rounded-2xl">
                    <Inbox className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div className="text-[13.5px] text-slate-600 font-medium">{emptyText}</div>
                </td>
              </tr>
            ) : pageRows.map((row, i) => (
              <tr
                key={row.id || i}
                className={
                  "transition-colors duration-150 " +
                  (onRowClick ? "cursor-pointer hover:bg-[#F8FAF7]" : "hover:bg-[#FBFDFB]")
                }
                onClick={() => onRowClick?.(row)}
                data-testid={`${testId}-row-${i}`}
                style={{ borderBottom: i < pageRows.length - 1 ? "1px solid #F5F8F5" : "none" }}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={
                      "px-5 py-3.5 text-slate-700 " +
                      (c.align === "end" ? "text-end tabular-nums " : "") +
                      (c.className || "")
                    }
                  >
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
                {actions && <td className="px-5 py-3 text-end whitespace-nowrap">{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > pageSize && (
        <div
          className="flex items-center justify-between px-5 py-3.5"
          style={{ borderTop: "1px solid #EEF3EF", background: "#F8FAF7" }}
        >
          <div className="text-[12px] text-slate-600">
            <span className="tabular-nums font-bold text-slate-900">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)}
            </span>
            <span className="text-slate-400"> من </span>
            <span className="tabular-nums font-bold text-slate-900">{filtered.length}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="h-8 w-8 p-0 rounded-lg border-slate-200"
              data-testid={`${testId}-prev`}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </Button>
            <span className="px-2 tabular-nums text-[12px] text-slate-600 font-semibold">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="h-8 w-8 p-0 rounded-lg border-slate-200"
              data-testid={`${testId}-next`}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
